import type { DesktopAgent } from '@finos/fdc3';

import { ConnectifiFDC3Api } from './api';
import {
  FDC3Agent,
  Logger,
  AgentConfig,
  ResolveCallbackProps,
  IntentResolutionMessage,
  ResolutionType,
  DownEventHandlers,
  DownResponseHandlerMap,
  DirectoryProps,
  AgentConfigInfo,
  RedirectStyle,
} from '../types';

import {
  DownEvent,
  DownEventData,
  DownEventTopic,
  DownResponse,
  DownResponseData,
  DownResponseTopic,
} from '@/common/interop/down-messages';
import { InteropStrategies } from '@/common/types';
import {
  TunnelResponse,
  TunnelResponseTopic,
  TunnelSessionErrorResponse,
} from '@/common/interop/tunnel';

import { SubscriptionManager } from './subscription';
import { Tunnel } from './tunnel';
import { AgentWebSocket } from './websocket';

import { Envelope } from '@/common/interop/envelope';
import { createLogger } from '../common/logger';
import { isPortTimeoutError, portTimeoutError } from '../common/port';
import {
  requestConsent,
  waitForStorageAccess,
  createStorageAccessIframe,
} from './storage-access';
import { createAppId } from '../common/app-id';
import {
  authenticate,
  AuthTargetType,
  getAuthenticationUrl,
} from './authentication';
import { hasStorageAccess } from './iframe';

// default/global fdc3 channel id
const GLOBAL_CHANNEL = 'global';

// pending id prefix - if pending id starts with this, we can reliably use window.name
const PENDING_ID_PREFIX = '_cfi_';

// error message when server rejects the app identity
export const APP_IDENTITY_ERROR_MSG = 'invalid app identity';

export class ConnectifiAgent implements FDC3Agent {
  // build/compile time variable
  version: string | undefined = process.env.AGENT_VERSION;

  // agent properties (createAgent/ctor args)
  config: AgentConfig;
  configInfo: AgentConfigInfo | undefined;

  // agent state
  currentChannel = GLOBAL_CHANNEL;
  // TODO - currently open resolver modal state !!!! ???
  // this is pendingEventId for an open intent resolver UI - these must be modal
  resolverPendingEventId: string | undefined;

  // fdc3 api - created/returned in createAgent
  fdc3: DesktopAgent;

  ws: AgentWebSocket;

  pendingId: string | undefined;

  logger: Logger;

  subManager = new SubscriptionManager();

  tunnel: Tunnel;

  hasNeverBeenConnected: boolean = true;

  constructor(
    private interopHost: string,
    private appName: string,
    private namespace: string,
    private directoryName: string,
    private port: MessagePort,
    config?: AgentConfig,
    configInfo?: AgentConfigInfo,
  ) {
    this.config = { ...config };
    this.configInfo = configInfo;

    this.logger = createLogger(config?.logLevel || 'info', config?.logger);

    this.ws = new AgentWebSocket(port, this.logger);
    this.tunnel = new Tunnel(
      port,
      this.logger,
      document.location.href,
      document.title,
    );

    this.fdc3 = new ConnectifiFDC3Api(
      this.ws,
      this.subManager,
      this.config.bridgeGlobal || false,
    );
  }

  handleWebsocketConnect(): void {
    // send version and pendingId on initial connect
    if (this.hasNeverBeenConnected) {
      this.ws.action('updateInstance', {
        pendingId: this.pendingId,
        version: this.version,
        config: this.configInfo,
      });
      window.name = '';
      this.config.onFDC3Ready?.(this.fdc3);
    }
    this.config.onConnected?.(this.hasNeverBeenConnected);
    this.hasNeverBeenConnected = false;
  }

  setWorkingState(workInProgress: boolean): void {
    this.config.onWorkingChanged?.call(this, workInProgress);
  }

  // start promise is also rejected when these events occur
  onAppIdentityError(): void {
    this.setWorkingState(false);
    this.config.onSessionError?.(APP_IDENTITY_ERROR_MSG);
  }

  handleTunnelTimeout(): void {
    this.setWorkingState(false);
    this.config.onSessionError?.(portTimeoutError.message);
  }

  mustTunnelInstanceId() {
    const instanceId = this.tunnel.instance?.id;
    if (!instanceId) {
      throw new Error('Tunnel instance ID is undefined.');
    }
    return instanceId;
  }

  async ensureStorageAccess() {
    if (await hasStorageAccess(this.port)) {
      return;
    }

    const button = createStorageAccessIframe(
      this.interopHost,
      createAppId(this.appName, this.namespace, this.directoryName),
    );
    await this.config.handleRequestStorageAccess?.({
      button,
      waitForStorageAccess,
      requestConsent: requestConsent.bind(
        undefined,
        this.interopHost,
        this.namespace,
        this.directoryName,
      ),
    });

    await this.ensureStorageAccess();
  }

  async connect() {
    return this.ws.connect(
      this.namespace,
      this.directoryName,
      this.mustTunnelInstanceId(),
    );
  }

  async handleWindowOnline() {
    this.logger.debug('browser online, connecting');
    await this.connect();
  }

  handleWindowOffline() {
    // destroy all reconnects, only online mode can save us now
    this.logger.debug(
      'browser offline, canceling reconnects, waiting for online event',
    );
    this.ws.disconnect();
  }

  handleLogoutComplete() {
    this.setWorkingState(false);
    this.config.onSignedOut?.();
    // reload tunnel to re-initialize login prompt
    return this.touchTunnelAndHandleResponse(true);
  }

  async handleStartSession(
    directory: string,
    owner: string,
    icon: string | undefined,
    interopStrategy: InteropStrategies,
  ) {
    const dirProps: DirectoryProps = {
      name: directory,
      interopStrategy,
    };
    if (icon) {
      dirProps.icon = `${this.interopHost}/${icon}`;
    }
    this.config.onSessionStarted?.(dirProps, owner);
    await this.connect();

    this.setWorkingState(false);
  }

  // TODO: Refactor login.
  async handleLogin(event: MessageEvent) {
    const { data } = event;
    if (typeof data !== 'string') return;
    try {
      const { topic } = JSON.parse(data);
      if (topic === 'logout') {
        this.handleLogoutComplete();
      }
    } catch (err) {
      // Ignore message unrelated to login.
    }
  }

  async handleSessionError(response: TunnelSessionErrorResponse) {
    const { directory, icon, interopStrategy } = response;

    this.logger.debug('session requires authentication', response);

    const dirProps: DirectoryProps = {
      name: directory,
      interopStrategy: interopStrategy as InteropStrategies,
    };
    if (icon) {
      dirProps.icon = `${this.interopHost}/${icon}`;
    }

    this.setWorkingState(false);

    this.config.onSessionAuthRequired?.(dirProps);

    const handleAuthenticate = this.config.handleAuthenticate;
    if (!handleAuthenticate) {
      return;
    }

    this.logger.debug('requesting and waiting for authentication ', response);

    await handleAuthenticate({
      ...dirProps,
      authenticate: authenticate.bind(undefined, this.interopHost),
      getAuthenticationUrl: (targetType?: AuthTargetType) => {
        return getAuthenticationUrl(this.interopHost, targetType);
      },
    });

    this.setWorkingState(true);
    this.config.onSignedIn?.();

    this.logger.debug(
      'reloading tunnel due to authentication success',
      response,
    );

    await this.touchTunnelAndHandleResponse();
  }

  handleIdentityError() {
    this.setWorkingState(false);
    this.onAppIdentityError();
    throw new Error(APP_IDENTITY_ERROR_MSG);
  }

  async handleTunnelResponse(response: TunnelResponse): Promise<void> {
    const isStartSession = response.topic === TunnelResponseTopic.StartSession;

    // prefer window.name if correct format, otherwise use the pendingId in the message
    this.pendingId = window.name.startsWith(PENDING_ID_PREFIX)
      ? window.name.substring(5)
      : isStartSession
      ? response.pendingId
      : undefined;

    const isSessionError = response.topic === TunnelResponseTopic.SessionError;

    const isDirectoryMismatch =
      (isStartSession || isSessionError) &&
      (this.directoryName !== response.directory ||
        this.namespace != response.namespace);
    if (isDirectoryMismatch) {
      const errMessage = `received response from tunnel with mismatching directory: expected ${this.directoryName}, got ${response.directory}`;
      this.logger.error(errMessage, response);
      throw new Error(errMessage);
    }

    if (isStartSession) {
      const { directory, owner, interopStrategy, icon } = response;
      await this.handleStartSession(
        directory,
        owner,
        icon,
        interopStrategy as InteropStrategies,
      );
    } else if (response.topic === TunnelResponseTopic.IdentityError) {
      this.handleIdentityError();
    } else if (isSessionError) {
      await this.handleSessionError(response);
    }
  }

  async touchTunnelAndHandleResponse(reset: boolean = false) {
    await this.ensureStorageAccess();
    if (reset) {
      this.tunnel.resetInstance();
    }
    const tunnelResponse = await this.tunnel.touch().catch((err) => {
      if (isPortTimeoutError(err)) {
        this.handleTunnelTimeout();
      } else {
        this.logger.error(`failed to touch tunnel`, err);
      }
      throw err;
    });
    return this.handleTunnelResponse(tunnelResponse);
  }

  async start() {
    window.addEventListener('message', this.handleLogin.bind(this));
    window.addEventListener('online', this.handleWindowOnline.bind(this));
    window.addEventListener('offline', this.handleWindowOffline.bind(this));

    this.ws.addEventListener('working', this.handleWebsocketWorking.bind(this));
    this.ws.addEventListener('connect', this.handleWebsocketConnect.bind(this));
    this.ws.addEventListener(
      'disconnect',
      this.handleWebsocketDisconnect.bind(this),
    );
    this.ws.addEventListener('message', this.handleWebsocketMessage.bind(this));

    await this.touchTunnelAndHandleResponse();

    return this.fdc3;
  }

  handleContextEvent(data: DownEventData['contextEvent']) {
    this.createPendingTitleCheck();
    this.subManager.callContextListeners(
      this.currentChannel,
      data.context,
      data.channel,
    );
    if (window.fdc3 && this.shouldBridgeGlobal()) {
      window.fdc3.broadcast(data.context);
    }
  }

  async handleIntentEvent(data: DownEventData['intentEvent']) {
    this.createPendingTitleCheck();
    const result = await this.subManager.callIntentListener(
      data.intent || '',
      data.context,
    );
    //send completeIntent back to service
    this.ws.action('completeIntent', {
      intent: data.intent,
      resultPendingId: data.resultPendingId,
      result: result,
    });
  }

  handleOpenEvent(data: DownEventData['openEvent']) {
    if (this.config.handleOpen) {
      return this.config.handleOpen.call(this, {
        name: data.name,
        appId: data.pendingId,
        url: data.url,
      });
    }
    if (data.url) {
      return window.open(data.url);
    }
  }

  handleJoinChannelResponse(data: DownResponseData['joinChannel']) {
    if (data.channel) {
      this.currentChannel = data.channel;
      this.config.onChannelJoined?.(data.channel);
    }
  }

  handleLeaveChannelResponse() {
    this.currentChannel = 'global';
    this.config.onChannelLeft?.();
  }

  sendIntentResolutionMessage(irm: IntentResolutionMessage) {
    this.setWorkingState(false);
    if (this.config.handleIntentResolution) {
      this.config.handleIntentResolution(
        irm,
        this.resolveResolver.bind(this),
        this.closeResolver.bind(this),
      );
    } else {
      this.logger.error('no resolver handler configured');
    }
  }

  async handleRaiseIntentResolutionEvent(
    data: DownEventData['intent-resolver'],
  ) {
    const { context, intent } = data;

    this.resolverPendingEventId = data.pendingEventId;
    const irm: IntentResolutionMessage = {
      resolutionType: ResolutionType.Intent,
      context,
      data,
    };

    if (intent && window.fdc3 && this.shouldBridgeGlobal()) {
      const appIntent = await window.fdc3.findIntent(intent.name, irm.context);

      irm.bridgeData = {
        intent,
        apps: appIntent.apps,
      };
    }

    this.sendIntentResolutionMessage(irm);
  }

  async handleRaiseIntentForContextResolutionEvent({
    pendingEventId,
    context,
    data,
  }: DownEventData['context-resolver']) {
    this.resolverPendingEventId = pendingEventId;
    const irm = {
      resolutionType: ResolutionType.Context,
      context,
      data,
    } as IntentResolutionMessage;

    if (window.fdc3 && this.shouldBridgeGlobal()) {
      const intentResult = await window.fdc3.findIntentsByContext(irm.context);
      irm.bridgeData = intentResult;
    }

    this.sendIntentResolutionMessage(irm);
  }

  eventHandlerMap: DownEventHandlers = {
    'context-resolver': (data) =>
      this.handleRaiseIntentForContextResolutionEvent(data),
    'intent-resolver': (data) => this.handleRaiseIntentResolutionEvent(data),
    intentEvent: (data) => this.handleIntentEvent(data),
    contextEvent: (data) => this.handleContextEvent(data),
    openEvent: (data) => this.handleOpenEvent(data),
    errorEvent: (data) =>
      this.logger.error('Received Error from server: ', data.error),
  };

  handleWebsocketEvent<TTopic extends DownEventTopic>({
    topic,
    data,
  }: DownEvent<TTopic>) {
    const handler = this.eventHandlerMap[topic];
    handler(data);
  }

  responseSpyHandlers: Partial<DownResponseHandlerMap> = {
    joinChannel: (data) => this.handleJoinChannelResponse(data),
    leaveCurrentChannel: () => this.handleLeaveChannelResponse(),
  };
  handleWebsocketResponse<TTopic extends DownResponseTopic>({
    topic,
    data,
  }: DownResponse<TTopic>) {
    const handler = this.responseSpyHandlers[topic];
    if (handler) handler(data);
  }

  handleWebsocketMessage(envelope: Envelope) {
    if (!envelope.eventId) {
      return this.handleWebsocketEvent(envelope as DownEvent<DownEventTopic>);
    }
    return this.handleWebsocketResponse(
      envelope as DownResponse<DownResponseTopic>,
    );
  }

  handleWebsocketWorking(isWorking: boolean) {
    this.setWorkingState(isWorking);
  }

  handleWebsocketDisconnect(retryInMillis: number) {
    this.config.onDisconnected?.(retryInMillis);
  }

  authenticate(redirectStyle?: RedirectStyle) {
    return authenticate(this.interopHost, redirectStyle);
  }

  //exit the current resolver state without resolving
  closeResolver() {
    this.ws.action('completeIntent', {
      resultPendingId: this.resolverPendingEventId!,
    });
  }

  createPendingTitleCheck() {
    const instance = this.tunnel.instance;
    if (!instance) return;
    setTimeout(() => {
      if (document.title === instance.title) return;

      this.logger.info(
        `updating instance title - old: ${instance.title}, new: ${document.title}`,
      );
      instance.title = document.title;
      this.ws.action('updateInstance', {
        title: document.title,
        url: document.location.href,
      });
    }, 500);
  }

  // resolution - app selected by user
  // raiseIntent to the bridge or send to interop server
  resolveResolver({
    context,
    intent,
    selected,
    bridge,
    metadata,
  }: ResolveCallbackProps) {
    if (bridge && window.fdc3) {
      this.logger.debug('opening with fdc3 global bridge', selected);
      window.fdc3.raiseIntent(intent, context, {
        appId: selected.appId,
      });
      return;
    }
    this.ws.action('resolveIntent', {
      selected: {
        name: selected.name,
        id: selected.id,
        type: selected.type,
      },
      intent: intent,
      context: context,
      pendingEventId: this.resolverPendingEventId!,
      metadata,
    });
  }

  shouldBridgeGlobal() {
    return this.config.bridgeGlobal || false;
  }
}
